import type { StatKey, StatPriority } from '../../types/classBuild'

/** Explicación general de las estadísticas. Los coeficientes exactos dependen de clase, habilidad y configuración. */
export const statInfo: { key: StatKey; name: string; short: string; aka: string[]; text: string }[] = [
  {
    key: 'str',
    name: 'Fuerza',
    short: 'STR',
    aka: ['strength'],
    text: 'Relacionada con el daño físico y con los requisitos de muchas armas y armaduras. No tiene la misma importancia en un guerrero que en un mago.',
  },
  {
    key: 'agi',
    name: 'Agilidad',
    short: 'AGI',
    aka: ['agility'],
    text: 'Interviene en la velocidad y en aspectos defensivos; según la clase, también en el daño y la precisión. No da el mismo beneficio por punto a todas las clases.',
  },
  {
    key: 'vit',
    name: 'Vitalidad',
    short: 'VIT',
    aka: ['vida', 'stamina', 'estamina'],
    text: 'Aumenta tu capacidad de supervivencia. Algunas habilidades pueden tener relaciones adicionales con ella.',
  },
  {
    key: 'ene',
    name: 'Energía',
    short: 'ENE',
    aka: ['energy'],
    text: 'Relacionada con el maná, los requisitos de habilidades, la magia y ciertos buffs. No es exclusiva de los magos.',
  },
  {
    key: 'cmd',
    name: 'Comando',
    short: 'CMD',
    aka: ['command'],
    text: 'Exclusiva del Dark Lord. Va ligada a sus mascotas (Dark Raven y Dark Horse) y a las funciones confirmadas en Vanguard.',
  },
]

export const AG_NOTE = 'Agilidad y AG no son lo mismo: AG es un recurso, como el maná, que consumen determinadas habilidades.'

export const priorityInfo: Record<StatPriority, { label: string; text: string; tone: string }> = {
  principal: { label: 'Principal', text: 'Concentra buena parte de los puntos disponibles para ese estilo.', tone: 'border-[#D4AF37] bg-[#D4AF37] text-[#050b14]' },
  complementaria: { label: 'Complementaria', text: 'Acompaña a la principal para equilibrar el personaje.', tone: 'border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#E8C860]' },
  supervivencia: { label: 'Según supervivencia', text: 'Súbela cuando el personaje no resiste el contenido.', tone: 'border-sky-400/50 bg-sky-400/10 text-sky-200' },
  requisitos: { label: 'Requisitos', text: 'Invierte lo necesario para tu equipo y tus habilidades. No significa cero puntos.', tone: 'border-white/20 bg-white/[0.04] text-gray-200' },
  confirmar: { label: 'Por confirmar', text: 'Depende de un escalado que aún no está comprobado en Vanguard.', tone: 'border-dashed border-white/25 bg-transparent text-gray-400' },
}

export const pointMethod: { title: string; text: string }[] = [
  { title: 'Elige tu forma de jugar', text: 'Daño físico, magia, apoyo o una combinación.' },
  { title: 'Revisa los requisitos', text: 'Del arma, el equipo y la habilidad que vas a usar.' },
  { title: 'Cumple esos requisitos primero', text: 'Antes de seguir cualquier recomendación general.' },
  { title: 'Refuerza la estadística principal', text: 'La de tu estilo de juego.' },
  { title: 'Ajusta agilidad y vitalidad', text: 'Según tu velocidad, tu resistencia y cómo te va en el spot.' },
  { title: 'Revisa la distribución', text: 'Cada vez que cambies de equipo, de habilidad principal o de etapa.' },
]

export type Situation = { id: string; label: string; advice: string[]; itemCheck?: boolean; supportStyles?: boolean }

/** Consejos, no cálculos: no hay fórmulas de daño comprobadas en Vanguard. */
export const situations: Situation[] = [
  {
    id: 'equipar',
    label: 'No puedo equipar mi arma',
    itemCheck: true,
    advice: [
      'Comprueba que el ítem es compatible con tu clase.',
      'Compara tu nivel y tu energía con los requisitos documentados del ítem.',
      'Los requisitos de fuerza y agilidad no vienen en la exportación del servidor: míralos en la ventana del ítem dentro del juego.',
    ],
  },
  {
    id: 'aprender',
    label: 'No puedo aprender el poder',
    advice: [
      'Revisa que el pergamino, orbe o libro sea de tu clase.',
      'Algunos poderes piden una evolución concreta.',
      'Compara tu nivel y tu energía con los requisitos documentados del ítem que enseña el poder.',
      'Los requisitos del ítem no son necesariamente los mismos que los del poder en sí.',
    ],
  },
  {
    id: 'lento',
    label: 'Mato lento pero sobrevivo',
    advice: [
      'Revisa si tu arma está a la altura del spot.',
      'Comprueba que usas la habilidad principal de tu estilo.',
      'Si ya cumples los requisitos, invierte en la estadística ofensiva principal de tu estilo.',
    ],
  },
  {
    id: 'muero',
    label: 'Muero demasiado',
    advice: [
      'Primero revisa el mapa: puede que el spot sea demasiado alto para tu nivel.',
      'Luego revisa tu equipo y tus defensas, y si tienes buffs de party.',
      'Por último, sube vitalidad.',
      'Casi nunca es culpa de una sola estadística.',
    ],
  },
  {
    id: 'recursos',
    label: 'Me quedo sin maná o AG',
    advice: [
      'Revisa cuánto consume tu habilidad principal y cómo recuperas maná o AG (pociones, pausas).',
      'La configuración del Helper todavía no está documentada en la guía.',
    ],
  },
  {
    id: 'party',
    label: 'Quiero apoyar a mi party',
    supportStyles: true,
    advice: [
      'Los estilos de apoyo fortalecen al grupo, pero tienen menos daño propio.',
      'Si juegas de apoyo, planifica tu progreso con una party: subir solo será más lento.',
    ],
  },
  {
    id: 'pvp',
    label: 'Quiero hacer PvP',
    advice: [
      'En PvP importan el equipo, las habilidades, la resistencia y el balance del servidor.',
      'No hay una distribución ganadora comprobada en Vanguard.',
      'Empieza por el estilo resistente de tu clase y ajústalo según lo que veas en combate.',
    ],
  },
]

/** Datos que faltan para recomendar cantidades exactas (ninguno viene en la exportación del 08/10/2026). */
export const missingForExact: string[] = [
  'Puntos por nivel, por evolución y por reset de cada clase (configuración de personajes del servidor).',
  'Requisitos reales de fuerza, agilidad, vitalidad y comando del equipo y de las habilidades. La exportación solo trae el nivel y la energía de cada ítem.',
  'Fórmulas de daño, velocidad, defensa y vida por clase.',
  'Cómo escalan las habilidades y los buffs con cada estadística.',
  'Fórmulas y requisitos del Dark Raven y el Dark Horse.',
  'Límites de estadísticas y cambios de balance propios de Vanguard.',
]

export const OFFICIAL_NOTE =
  'Las páginas oficiales actuales incluyen renovaciones de temporadas posteriores. Esta guía es de Season 6: no se importan habilidades, fórmulas ni sistemas de esas renovaciones.'
